import React, { useMemo } from "react";
import { useRecoilState } from "recoil";
import ScreenCard from "../ScreenCard";
import { newMissionState } from "../../atoms/core";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import MissionDuration from "../../components/editors/MissionDuration";
import { addDays } from "date-fns";
import { Repetition, Unit } from "../../__generated__/graphql";
import { Bubble } from "../../components/Bubble";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";

const CreateMissionDuration = () => {
    const i18n = useI18n();
    const [mission, setMission] = useRecoilState(newMissionState);
    const { validateScreen } = React.useContext(CreateMissionScreensContext);
    const [isCdi, setIsCdi] = React.useState<boolean>(!mission.currentContract?.endDate);

    const setStartDate = React.useCallback(
        (startDate: Date | null) => {
            setMission((oldMission) => {
                const endDate = oldMission.currentContract?.endDate;
                let newEndDate;
                if (startDate) {
                    if (endDate) {
                        newEndDate = new Date(endDate);
                    }
                    if (
                        oldMission.currentContract?.workingTimeClassicSpec?.repetition ===
                            Repetition.None &&
                        oldMission.currentContract?.workingTimeClassicSpec?.unit === Unit.Days &&
                        oldMission.currentContract?.workingTimeClassicSpec?.unitCount
                    ) {
                        // special case when we have a mission with fixed number of days
                        // we auto set the end date.
                        newEndDate = addDays(
                            new Date(startDate),
                            oldMission.currentContract?.workingTimeClassicSpec?.unitCount - 1,
                        );
                    }
                    if (newEndDate && newEndDate < startDate) {
                        newEndDate = startDate;
                    }
                }

                return {
                    ...oldMission,
                    currentContract: {
                        ...oldMission.currentContract,
                        startDate: startDate?.toISOString().substring(0, 10) || null,
                        endDate: newEndDate?.toISOString().substring(0, 10) || null,
                        effectiveEndDate: newEndDate?.toISOString().substring(0, 10) || null,
                    },
                };
            });
        },
        [setMission],
    );
    const setEndDate = React.useCallback(
        (endDate: Date | null) => {
            setMission({
                ...mission,
                currentContract: {
                    ...mission.currentContract,
                    endDate: endDate ? endDate.toISOString().substring(0, 10) : null,
                    effectiveEndDate: endDate ? endDate.toISOString().substring(0, 10) : null,
                },
            });
        },
        [mission, setMission],
    );
    const validate = React.useCallback(() => {
        void validateScreen("CreateMissionDuration");
    }, [validateScreen]);

    const isValid = useMemo(
        () =>
            isCdi
                ? !!mission.currentContract?.startDate
                : !!mission.currentContract?.startDate && !!mission.currentContract?.endDate,
        [isCdi, mission.currentContract?.startDate, mission.currentContract?.endDate],
    );

    const origin = useBubbleStepOrigin("CreateMissionDuration");

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.translate("next"),
                    onPress: validate,
                    isValid: isValid,
                },
            ]}
        >
            <Bubble title={i18n.t("screens.user.create_mission_duration.title")} origin={origin}>
                <MissionDuration
                    isCdi={isCdi}
                    mission={mission}
                    setIsCdi={setIsCdi}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                />
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionDuration;
